import { formatInTimeZone } from 'date-fns-tz';

import { replaceTokens } from '@dte/otw-utils-core/src/strings/replaceTokens';

import type { DateRepresentation } from '../DateRepresentation';
import { defaultTimeZone } from '../defaultTimeZone';
import { safeParseDate } from '../parse/date';

// Months in AP Style
const monthSubstitutions: Record<string, string> = {
	January: 'Jan.',
	February: 'Feb.',
	August: 'Aug.',
	September: 'Sept.',
	October: 'Oct.',
	November: 'Nov.',
	December: 'Dec.',
};

function includesShortMonth(dateFormat: string): boolean {
	return dateFormat.includes('MMM') && !dateFormat.includes('MMMM');
}

function abberviateMonths(dateString: string): string | undefined {
	return replaceTokens(dateString, monthSubstitutions);
}

export interface FormatInTzOptions {
	useAPStyleShortMonth?: boolean;
	replaceTodayWithString?: boolean;
	replaceNoonWithString?: boolean;
	replaceMidnightWithString?: boolean;
}

const formattingDefaults: FormatInTzOptions = {
	useAPStyleShortMonth: true,
	replaceTodayWithString: false,
	replaceNoonWithString: false,
	replaceMidnightWithString: false,
};

export function formatInTz(
	date: DateRepresentation,
	dateFormat: string,
	options?: FormatInTzOptions,
	timeZone: string = defaultTimeZone,
): string | undefined {
	const dateValue = safeParseDate(date);
	if (!dateValue) {
		return undefined;
	}

	let formatToUse = dateFormat;

	const mergedOptions: FormatInTzOptions = {
		...formattingDefaults,
		...options,
	};

	// Determine if we need custom logic to replace short months
	const shortMonth = mergedOptions.useAPStyleShortMonth && includesShortMonth(formatToUse);

	// If using short months and the date format contains 'MMMM', start by formatting the date with the full month name (then do a replacement with the abbreviation afterwards)
	if (shortMonth) {
		formatToUse = formatToUse.replace('MMM', 'MMMM');
	}

	try {
		const formatted = formatInTimeZone(dateValue, timeZone, formatToUse);

		// Go back and replace the full month name with the abbreviated version
		if (shortMonth) {
			return abberviateMonths(formatted);
		}

		return formatted;
	} catch {
		// Unable to format date
	}

	return undefined;
}

export function formatEST(
	date: DateRepresentation,
	dateFormat: string,
	options?: FormatInTzOptions,
): string | undefined {
	return formatInTz(date, dateFormat, options, 'America/New_York');
}
